'use client';

import React, { forwardRef, ForwardedRef, useId } from 'react';
import classNames from 'classnames';
import './css/CustomRadioGroup.scss';
import { Helper } from '../Helper';
import { Error } from '../Error';
import { CustomRadioProps } from '../CustomRadio/CustomRadio';

export interface CustomRadioGroupProps
  extends React.HTMLAttributes<HTMLElement> {
  disabled?: boolean;
  error?: string;
  helper?: string;
  isErrorAnnounced?: boolean;
  isStacked?: boolean;
  legend?: string;
  name?: string;
  required?: boolean;
}

export const CustomRadioGroup = forwardRef(
  (props: CustomRadioGroupProps, ref: ForwardedRef<HTMLFieldSetElement>) => {
    const {
      className,
      disabled,
      error,
      helper,
      isErrorAnnounced = true,
      isStacked,
      legend,
      name: nameProp,
      required,
      children,
      ...spreadProps
    } = props;

    const uniqueId = useId();
    const name = nameProp || `${uniqueId}-custom-radio-group`;
    const errorId = error ? `${uniqueId}-error` : '';
    const helperId = helper ? `${uniqueId}-helper` : '';
    const ariaDescribedByValue = `${errorId} ${helperId}`.trim() || null;

    return (
      <fieldset
        {...spreadProps}
        className={classNames({
          'ps-custom-radio-group': true,
          'ps-custom-radio-group-error': Boolean(error),
          'ps-custom-radio-group-is-stacked': isStacked,
          [`${className}`]: className,
        })}
        disabled={disabled}
        ref={ref}
      >
        <legend>{legend}</legend>

        {helper && (
          <Helper
            id={helperId}
            className="ps-custom-radio-group-helper"
          >
            {helper}
          </Helper>
        )}

        <div className="ps-custom-radio-group-content">
          {React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) {
              return child;
            }
            return React.cloneElement(child, {
              // @ts-expect-error - ts is not recognizing this attribute on the child element
              'aria-describedby': ariaDescribedByValue,
              name,
              'disabled':
                disabled || (child.props as CustomRadioProps).disabled,
              'required':
                required || (child.props as CustomRadioProps).required,
              'hasError': !!error,
            });
          })}
        </div>
        {error && (
          <Error
            id={errorId}
            className="ps-custom-radio-group-error"
            isErrorAnnounced={isErrorAnnounced}
          >
            {error}
          </Error>
        )}
      </fieldset>
    );
  }
);
